import { Message } from '~/components/Chat/types';
import { MOBILE_PHONE_ERROR_KEYS } from '~/services/modules/users/types';
import { storeState } from '~/store';
import { isValidBrazilianMobile, onlyPhoneDigits, userHasNoPhone } from '~/utils/phoneValidation';
import {
  codeDeliveryMethodMessages,
  cpfMessages,
  emailMessages,
  equipmentMessages,
  genderAfterCodeMessages,
  genderMessages,
  nameMessages,
  nextAcessoriesValueMessages,
  nextCompetionsMessages,
  nextEquipmentMaterialMessages,
  nextEquipmentValueMessages,
  nextInternationalCoverageMessages,
  phoneAfterCodeMessages,
  userConfirmationMessages
} from './handleMessages';

/**
 * Steps do fluxo de cadastro encontrado. Não fazem parte do array `steps` que vem da API,
 * por isso precisam de navegação própria no avançar e no voltar.
 */
export const STEP_CPF = 3;
export const STEP_GENDER = 4;
export const STEP_EQUIPMENT = 5;
export const STEP_USER_CONFIRMATION = 11;
export const STEP_CODE_VERIFICATION = 12;
export const STEP_CODE_DELIVERY_METHOD = 13;
/** CORE-1025: cadastro encontrado sem telefone — o celular é pedido depois do código validado. */
export const STEP_PHONE = 14;

const HTTP_UNAUTHORIZED = 401;
const DEFAULT_SAVE_PHONE_ERROR = 'Não foi possível salvar o seu celular. Tente novamente.';
const SESSION_EXPIRED_ERROR = 'Sua sessão expirou. Valide o código de verificação novamente.';

interface IHandleStepsProps {
  setDisableStepHandler: (value: boolean) => void;
  setMessages: (messages: any) => void;
  MESSAGES: Message[] | any[];
  setStepChat: (value: number) => void;
  removeMessage: (index: number) => void;
  internationalCoverage: boolean;
  equipment: string;
  name: string;
  email: string;
  cpf: string;
  checkUserDetails: (document: string) => Promise<any>;
  sendCodeToUser: (document: string, codeType: number) => Promise<any>;
  setUserData: (userData: any) => void;
  setCustomerId: (customerId: string) => void;
  /** CORE-1025: grava o celular (só dígitos) com o token do validate-code. */
  savePhoneToUser?: (phone: string) => Promise<unknown>;
  /** CORE-1025: recebe o valor do input de celular e se ele é válido. */
  onPhoneChange?: (phone: string, isValid: boolean) => void;
}

/** Permite ao botão de voltar reabrir os steps do cadastro encontrado com os callbacks corretos. */
export interface IUserConfirmationFlow {
  showUserConfirmation?: () => void;
  showCodeDeliveryMethod?: () => void;
  /** true quando o equipamento foi alcançado pelo login — nesse caminho o gênero não é perguntado */
  cameFromUserConfirmation?: boolean;
}

export interface IMessage extends Pick<IHandleStepsProps, 'MESSAGES' | 'setMessages' | 'setDisableStepHandler'> {}

interface IHandleNextStepProps {
  currentStep: number;
  steps?: number[];
  setStepChat: (value: number) => void;
}

export const handleNextStep = ({ setStepChat, currentStep, steps = [] }: IHandleNextStepProps) => {
  if (currentStep === steps[steps.length - 1]) {
    setStepChat(0);
  }

  const nextStepChat = steps.find((num: number) => num > currentStep) || 0;

  setStepChat(nextStepChat);
};

export const handlePreviousStep = (
  { setStepChat, currentStep, steps = [] }: IHandleNextStepProps,
  params: IMessage,
  removeMessage: (index: number) => void,
  equipment: string,
  name: string,
  userConfirmationFlow: IUserConfirmationFlow = {}
) => {
  const { showUserConfirmation, showCodeDeliveryMethod, cameFromUserConfirmation } = userConfirmationFlow;

  const backToCpf = () => {
    cpfMessages({ ...params }, removeMessage);
    setStepChat(STEP_CPF);
  };

  const backToUserConfirmation = (show: () => void) => {
    setStepChat(STEP_USER_CONFIRMATION);
    show();
  };

  // Código de verificação: volta para a escolha do canal, para trocar SMS por e-mail quando o código não chega
  if (currentStep === STEP_CODE_VERIFICATION) {
    if (showCodeDeliveryMethod) {
      setStepChat(STEP_CODE_DELIVERY_METHOD);
      showCodeDeliveryMethod();
      return;
    }
    backToCpf();
    return;
  }

  // Escolha do canal do código: volta para a confirmação do cadastro encontrado
  if (currentStep === STEP_CODE_DELIVERY_METHOD) {
    if (showUserConfirmation) {
      backToUserConfirmation(showUserConfirmation);
      return;
    }
    backToCpf();
    return;
  }

  // Telefone pedido após o código: o código já foi consumido, então volta para a confirmação do cadastro
  if (currentStep === STEP_PHONE) {
    if (showUserConfirmation) {
      backToUserConfirmation(showUserConfirmation);
      return;
    }
    backToCpf();
    return;
  }

  // Confirmação do cadastro encontrado: volta para o CPF
  if (currentStep === STEP_USER_CONFIRMATION) {
    backToCpf();
    return;
  }

  // Equipamento alcançado pelo login: o gênero não foi perguntado, então volta para a confirmação
  if (currentStep === STEP_EQUIPMENT && cameFromUserConfirmation && showUserConfirmation) {
    backToUserConfirmation(showUserConfirmation);
    return;
  }

  const stepsHandlers: { [key: number]: () => void } = {
    1: () => {
      setStepChat(1);
    },
    2: () => {
      emailMessages({ ...params }, removeMessage, name);
      setStepChat(2);
    },
    3: () => {
      cpfMessages({ ...params }, removeMessage);
      setStepChat(3);
    },
    4: () => {
      genderMessages({ ...params }, removeMessage);
      setStepChat(4);
    },
    5: () => {
      equipmentMessages({ ...params }, removeMessage);
      setStepChat(5);
    },
    6: () => {
      nextEquipmentValueMessages({ ...params }, equipment);
      setStepChat(6);
    },
    7: () => {
      nextAcessoriesValueMessages({ ...params }, equipment);
      setStepChat(7);
    },
    8: () => {
      nextEquipmentMaterialMessages({ ...params });
      setStepChat(8);
    },
    9: () => {
      nextCompetionsMessages({ ...params }, name);
      setStepChat(9);
    },
    10: () => {
      nextInternationalCoverageMessages({ ...params }, name);
      setStepChat(10);
    }
  };

  // stepChat 0 = fluxo respondido até o fim, então o anterior é o último step que a API retornou
  const orderedSteps = [...steps].sort((a: number, b: number) => a - b);
  const previousStep =
    currentStep === 0
      ? orderedSteps[orderedSteps.length - 1]
      : orderedSteps.filter((num: number) => num < currentStep).pop();

  if (previousStep !== undefined && stepsHandlers[previousStep]) {
    stepsHandlers[previousStep]();
  }
};

export const handleSteps = ({
  setDisableStepHandler,
  setMessages,
  MESSAGES,
  setStepChat,
  removeMessage,
  equipment,
  name,
  cpf,
  checkUserDetails,
  sendCodeToUser,
  setUserData,
  setCustomerId,
  savePhoneToUser,
  onPhoneChange = () => undefined
}: IHandleStepsProps) => {
  const handleName = async () => nameMessages({ MESSAGES, setDisableStepHandler, setMessages });

  const handleNextEmail = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });
    emailMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage, name);
  };

  /**
   * Monta os callbacks do fluxo de cadastro encontrado (steps 11 -> 13 -> 12) para um `userData`.
   * É reconstruído a cada entrada no fluxo — inclusive pelo botão de voltar — para que o código
   * seja sempre enviado/validado com o documento que está em tela.
   */
  const buildUserConfirmationFlow = (userData: any) => {
    // Cadastro negado ou não encontrado: um customer_id de validação anterior não vale para esta cotação
    const goToGender = () => {
      setCustomerId('');
      setStepChat(STEP_GENDER);
      genderMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
    };

    const handleMethodSelected = async (method: number) => {
      try {
        setDisableStepHandler(true);

        // Chama a API para enviar código (1 = SMS, 2 = Email)
        await sendCodeToUser(userData.document_number, method);

        // Se o código foi enviado com sucesso, vai para o step de verificação de código
        setStepChat(STEP_CODE_VERIFICATION);
      } catch (error: any) {
        setDisableStepHandler(false);

        const errorMessage =
          error?.response?.data?.notifications?.[0]?.message ||
          'Erro ao enviar código de verificação. Tente novamente.';

        storeState.addToast({
          title: 'Erro',
          message: errorMessage,
          type: 'error'
        });
      }
    };

    const showCodeDeliveryMethod = () => {
      codeDeliveryMethodMessages(
        { MESSAGES, setDisableStepHandler, setMessages },
        name,
        handleMethodSelected,
        userHasNoPhone(userData)
      );
    };

    const handleConfirm = async () => {
      setStepChat(STEP_CODE_DELIVERY_METHOD);
      showCodeDeliveryMethod();
    };

    const handleNewCpfSubmit = async (newCpf: string) => {
      try {
        setDisableStepHandler(true);
        const newUserData = await checkUserDetails(newCpf);

        if (newUserData) {
          // O novo CPF passa a ser a origem do envio e da validação do código
          setUserData(newUserData);
          buildUserConfirmationFlow(newUserData).showUserConfirmation();
        } else {
          goToGender();
        }
      } catch (error) {
        goToGender();
      }
    };

    const showUserConfirmation = () => {
      userConfirmationMessages(
        { MESSAGES, setDisableStepHandler, setMessages },
        userData,
        name,
        handleConfirm,
        goToGender,
        handleNewCpfSubmit
      );
    };

    return { showUserConfirmation, showCodeDeliveryMethod };
  };

  const handleAfterCpf = async (currentStep: number, steps: number[]) => {
    // Sem cadastro encontrado (ou com erro na consulta), segue o fluxo normal para o gênero
    const goToNextStep = () => {
      handleNextStep({ setStepChat, currentStep, steps });
      genderMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
    };

    // Um CPF novo invalida a validação anterior: o código precisa ser validado de novo para o titular valer
    setCustomerId('');

    try {
      const userData = await checkUserDetails(cpf);

      if (!userData) {
        goToNextStep();
        return;
      }

      // Guarda os dados do usuário para uso posterior
      setUserData(userData);
      setStepChat(STEP_USER_CONFIRMATION);
      buildUserConfirmationFlow(userData).showUserConfirmation();
    } catch (error) {
      goToNextStep();
    }
  };
  const handleNextCpf = async (currentStep: number, steps: number[]) => {
    // Primeiro exibe o input de CPF normalmente
    handleNextStep({ setStepChat, currentStep, steps });
    cpfMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
  };

  const handleNextGender = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    genderMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
  };

  const handleNextEquipment = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    equipmentMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
  };

  const handleNextEquipmentValue = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    nextEquipmentValueMessages({ MESSAGES, setDisableStepHandler, setMessages }, equipment);
  };

  const handleNextAccessoriesValue = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    nextAcessoriesValueMessages({ MESSAGES, setDisableStepHandler, setMessages }, equipment);
  };

  const handleNextEquipmentMaterial = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    nextEquipmentMaterialMessages({ MESSAGES, setDisableStepHandler, setMessages });
  };

  const handleNextCompetitions = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    nextCompetionsMessages({ MESSAGES, setDisableStepHandler, setMessages }, name);
  };

  const handleNextInternationalCoverage = async (currentStep: number, steps: number[]) => {
    handleNextStep({ setStepChat, currentStep, steps });

    nextInternationalCoverageMessages({ MESSAGES, setDisableStepHandler, setMessages }, name);
  };

  const handleNextSteps = ({ currentStep, steps = [] }: any) => {
    const stepsHandlers: { [key: number]: () => void } = {
      1: () => handleName(),
      2: () => handleNextEmail(currentStep, steps),
      3: () => handleNextCpf(currentStep, steps),
      4: () => handleNextGender(currentStep, steps),
      5: () => handleNextEquipment(currentStep, steps),
      6: () => handleNextEquipmentValue(currentStep, steps),
      7: () => handleNextAccessoriesValue(currentStep, steps),
      8: () => handleNextEquipmentMaterial(currentStep, steps),
      9: () => handleNextCompetitions(currentStep, steps),
      10: () => handleNextInternationalCoverage(currentStep, steps)
    };

    const nextStep = steps.find((num: number) => num > currentStep);

    if (nextStep !== undefined) {
      stepsHandlers[nextStep]();
    }

    if (!nextStep) {
      setStepChat(0);
    }
  };

  const goToGenderAfterCode = () => {
    setStepChat(STEP_GENDER);
    setTimeout(() => {
      genderAfterCodeMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
    }, 500);
  };

  const goToEquipmentAfterCode = () => {
    setStepChat(STEP_EQUIPMENT);
    setTimeout(() => {
      equipmentMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage);
    }, 500);
  };

  /** Depois do telefone (ou sem precisar dele): pede o género quando falta, senão segue para o equipamento. */
  const continueAfterPhone = (gender?: string) => {
    if (!gender) {
      goToGenderAfterCode();
      return;
    }
    goToEquipmentAfterCode();
  };

  /**
   * Código validado: cadastro sem telefone pede o celular (CORE-1025); com telefone, segue o fluxo de hoje
   * (género vazio no cadastro -> pergunta o género; senão -> equipamento).
   */
  const handleAfterCodeValidated = (result: { gender?: string }, userData: any) => {
    if (userHasNoPhone(userData)) {
      setStepChat(STEP_PHONE);
      setTimeout(() => {
        phoneAfterCodeMessages({ MESSAGES, setDisableStepHandler, setMessages }, removeMessage, onPhoneChange);
      }, 500);
      return;
    }

    continueAfterPhone(result.gender);
  };

  /** Grava o celular informado no passo do telefone. Devolve `true` quando o fluxo pôde seguir. */
  const handlePhoneSubmit = async (phone: string, gender: string | undefined, userData: any): Promise<boolean> => {
    if (!isValidBrazilianMobile(phone) || !savePhoneToUser) return false;

    setDisableStepHandler(true);
    try {
      await savePhoneToUser(onlyPhoneDigits(phone));
      continueAfterPhone(gender);
      return true;
    } catch (error: any) {
      const notification = error?.response?.data?.notifications?.[0];

      // Já tem telefone (ex.: cadastrado noutra aba): nada a gravar, segue sem travar
      if (notification?.key === MOBILE_PHONE_ERROR_KEYS.ALREADY_REGISTERED) {
        continueAfterPhone(gender);
        return true;
      }

      setDisableStepHandler(false);

      // Token do validate-code expirado: o titular deixa de valer e o código precisa ser pedido de novo
      if (error?.response?.status === HTTP_UNAUTHORIZED) {
        storeState.addToast({ title: 'Erro', message: SESSION_EXPIRED_ERROR, type: 'error' });
        setCustomerId('');
        setStepChat(STEP_CODE_DELIVERY_METHOD);
        buildUserConfirmationFlow(userData).showCodeDeliveryMethod();
        return false;
      }

      storeState.addToast({
        title: 'Erro',
        message: notification?.message || DEFAULT_SAVE_PHONE_ERROR,
        type: 'error'
      });
      return false;
    }
  };

  return {
    handleName,
    handleNextSteps,
    handleAfterCpf,
    buildUserConfirmationFlow,
    handleAfterCodeValidated,
    handlePhoneSubmit
  };
};
